// Super Tooltip Plugin - Enhanced Component Interactions
export function registerSuperTooltip(editor) {
  console.log('🎯 Registering Super Tooltip plugin...')
  
  const canvas = editor.Canvas
  const blockManager = editor.BlockManager
  const domComponents = editor.DomComponents
  
  // Configuration
  const config = {
    enabled: true,
    showTooltips: true,
    showFloatingButtons: true,
    animationDuration: 200,
    tooltipDelay: 300,
    buttonSize: 36,
    zIndex: 99999
  }
  
  // State management
  let currentTooltip = null
  let currentFloatingButtons = null
  let currentContextMenu = null
  let selectedComponent = null
  let tooltipTimeout = null
  
  // Create floating buttons container
  function createFloatingButtons() {
    console.log('🎯 Creating floating buttons...')
    const container = document.createElement('div')
    container.className = 'super-tooltip-floating-buttons'
    container.style.cssText = `
      position: fixed;
      z-index: ${config.zIndex};
      display: none;
      pointer-events: none;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      padding: 4px;
    `
    
    // Add Component button
    const addBtn = createFloatingButton('+', 'Add Component (Cmd/Ctrl + D)', () => {
      console.log('🎯 Add button clicked')
      showBlockInsertionMenu()
    })
    
    // More Options button
    const moreBtn = createFloatingButton('⋯', 'More Options (Cmd/Ctrl + F)', () => {
      console.log('🎯 More options button clicked')
      showContextMenu()
    })
    
    container.appendChild(addBtn)
    container.appendChild(moreBtn)
    
    console.log('✅ Floating buttons created')
    return container
  }
  
  // Create individual floating button
  function createFloatingButton(icon, tooltip, onClick) {
    const button = document.createElement('button')
    button.className = 'super-tooltip-floating-btn'
    button.innerHTML = icon
    button.title = tooltip
    button.style.cssText = `
      width: ${config.buttonSize}px;
      height: ${config.buttonSize}px;
      border: none;
      border-radius: 50%;
      background: #2563eb;
      color: white;
      cursor: pointer;
      font-size: 16px;
      font-weight: bold;
      margin: 2px;
      pointer-events: auto;
      transition: all 0.2s ease;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      border: 2px solid white;
    `
    
    button.addEventListener('mouseenter', () => {
      button.style.transform = 'scale(1.1)'
      button.style.background = '#1d4ed8'
    })
    
    button.addEventListener('mouseleave', () => {
      button.style.transform = 'scale(1)'
      button.style.background = '#2563eb'
    })
    
    button.addEventListener('click', (e) => {
      e.stopPropagation()
      onClick()
    })
    
    return button
  }
  
  // Show floating buttons near selected component
  function showFloatingButtons(component) {
    console.log('🎯 showFloatingButtons called with component:', component)
    if (!config.showFloatingButtons || !component) {
      console.log('❌ Floating buttons disabled or no component')
      return
    }
    
    hideFloatingButtons()
    
    const componentEl = component.getEl()
    if (!componentEl) {
      console.log('❌ Component element not found')
      return
    }
    
    console.log('🎯 Component element found:', componentEl)
    console.log('🎯 Component element classes:', componentEl.className)
    
    // `rect` is relative to the canvas iframe's own viewport, not the outer
    // page. The buttons are appended to the outer document.body below and
    // positioned with `position: fixed`, so the iframe's own offset in the
    // outer page has to be added in before they'll line up with the
    // component the user actually selected.
    const rect = componentEl.getBoundingClientRect()
    const frameRect = canvas.getFrameEl().getBoundingClientRect()

    console.log('🎯 Component rect:', rect)
    console.log('🎯 Window dimensions:', { width: window.innerWidth, height: window.innerHeight })

    const buttons = createFloatingButtons()
    document.body.appendChild(buttons)

    // Position buttons to the right of the component using fixed positioning
    const left = frameRect.left + rect.right + 10
    const top = frameRect.top + rect.top + (rect.height / 2) - (config.buttonSize / 2)
    
    console.log('🎯 Positioning buttons at:', { left, top })
    
    buttons.style.left = `${left}px`
    buttons.style.top = `${top}px`
    buttons.style.display = 'block'
    
    // Animate in
    buttons.style.opacity = '0'
    buttons.style.transform = 'translateX(-10px)'
    
    setTimeout(() => {
      buttons.style.transition = 'all 0.2s ease'
      buttons.style.opacity = '1'
      buttons.style.transform = 'translateX(0)'
    }, 10)
    
    currentFloatingButtons = buttons
    console.log('✅ Floating buttons shown')
  }
  
  // Hide floating buttons
  function hideFloatingButtons() {
    if (currentFloatingButtons) {
      console.log('🎯 Hiding floating buttons')
      currentFloatingButtons.style.transition = 'all 0.2s ease'
      currentFloatingButtons.style.opacity = '0'
      currentFloatingButtons.style.transform = 'translateX(-10px)'
      
      setTimeout(() => {
        if (currentFloatingButtons && currentFloatingButtons.parentNode) {
          currentFloatingButtons.parentNode.removeChild(currentFloatingButtons)
        }
        currentFloatingButtons = null
      }, config.animationDuration)
    }
  }
  
  // Show tooltip
  function showTooltip(element, text, position = 'top') {
    if (!config.showTooltips) return
    
    hideTooltip()
    
    const tooltip = document.createElement('div')
    tooltip.className = 'super-tooltip'
    tooltip.textContent = text
    tooltip.style.cssText = `
      position: absolute;
      background: #2c3e50;
      color: white;
      padding: 8px 12px;
      border-radius: 4px;
      font-size: 12px;
      z-index: ${config.zIndex + 1};
      pointer-events: none;
      white-space: nowrap;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
      opacity: 0;
      transition: opacity 0.2s ease;
    `
    
    document.body.appendChild(tooltip)
    
    const rect = element.getBoundingClientRect()
    let left, top
    
    switch (position) {
      case 'top':
        left = rect.left + (rect.width / 2) - (tooltip.offsetWidth / 2)
        top = rect.top - tooltip.offsetHeight - 8
        break
      case 'bottom':
        left = rect.left + (rect.width / 2) - (tooltip.offsetWidth / 2)
        top = rect.bottom + 8
        break
      case 'left':
        left = rect.left - tooltip.offsetWidth - 8
        top = rect.top + (rect.height / 2) - (tooltip.offsetHeight / 2)
        break
      case 'right':
        left = rect.right + 8
        top = rect.top + (rect.height / 2) - (tooltip.offsetHeight / 2)
        break
    }
    
    tooltip.style.left = `${left}px`
    tooltip.style.top = `${top}px`
    
    setTimeout(() => {
      tooltip.style.opacity = '1'
    }, 10)
    
    currentTooltip = tooltip
  }
  
  // Hide tooltip
  function hideTooltip() {
    if (currentTooltip) {
      currentTooltip.style.opacity = '0'
      setTimeout(() => {
        if (currentTooltip && currentTooltip.parentNode) {
          currentTooltip.parentNode.removeChild(currentTooltip)
        }
        currentTooltip = null
      }, config.animationDuration)
    }
  }
  
  // Show block insertion menu
  function showBlockInsertionMenu() {
    hideContextMenu()
    
    const menu = document.createElement('div')
    menu.className = 'super-tooltip-block-menu'
    menu.style.cssText = `
      position: fixed;
      background: #ffffff;
      border: 2px solid #2563eb;
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.3);
      z-index: ${config.zIndex + 2};
      width: 280px;
      max-height: 400px;
      overflow-y: auto;
      padding: 12px;
      backdrop-filter: blur(10px);
      box-sizing: border-box;
    `
    
    // Search input
    const searchInput = document.createElement('input')
    searchInput.type = 'text'
    searchInput.placeholder = '🔍 Search blocks...'
    searchInput.style.cssText = `
      width: 100%;
      padding: 8px 12px;
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      margin-bottom: 12px;
      font-size: 14px;
      background: #f9fafb;
      color: #374151;
      font-weight: 500;
      transition: all 0.2s ease;
      box-sizing: border-box;
    `
    
    searchInput.addEventListener('focus', () => {
      searchInput.style.borderColor = '#2563eb'
      searchInput.style.background = '#ffffff'
    })
    
    searchInput.addEventListener('blur', () => {
      searchInput.style.borderColor = '#e5e7eb'
      searchInput.style.background = '#f9fafb'
    })
    
    // Blocks container
    const blocksContainer = document.createElement('div')
    blocksContainer.className = 'super-tooltip-blocks'
    
    // Get all blocks
    const blocks = blockManager.getAll()
    
    function renderBlocks(filter = '') {
      blocksContainer.innerHTML = ''
      
      blocks.forEach(block => {
        if (filter && !block.get('label').toLowerCase().includes(filter.toLowerCase())) {
          return
        }
        
        const blockEl = document.createElement('div')
        blockEl.className = 'super-tooltip-block-item'
        blockEl.style.cssText = `
          display: flex;
          align-items: center;
          padding: 8px 12px;
          cursor: pointer;
          border-radius: 6px;
          margin-bottom: 2px;
          transition: all 0.2s ease;
          border: 1px solid transparent;
          box-sizing: border-box;
        `
        
        // Block icon/preview
        const icon = document.createElement('div')
        icon.style.cssText = `
          width: 32px;
          height: 32px;
          margin-right: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f3f4f6;
          border-radius: 6px;
          font-size: 16px;
          border: 1px solid #e5e7eb;
          flex-shrink: 0;
          overflow: hidden;
        `

        const media = block.get('media')
        if (media) {
          const trimmed = media.trim()
          if (trimmed.startsWith('<svg') || trimmed.startsWith('<img')) {
            // SVG/HTML media — render directly, not as img src
            icon.innerHTML = trimmed
            const svgEl = icon.querySelector('svg')
            if (svgEl) {
              svgEl.style.cssText = 'width: 20px; height: 20px; display: block;'
            }
          } else {
            // URL — use as img src
            const img = document.createElement('img')
            img.src = media
            img.style.cssText = 'width: 20px; height: 20px; object-fit: contain;'
            icon.appendChild(img)
          }
        } else {
          icon.textContent = '▦'
        }
        
        // Block label
        let labelText = block.get('label');
        if (typeof labelText !== 'string') {
          // Try to extract text if it's HTML or an object
          if (labelText && labelText.props && labelText.props.children) {
            labelText = String(labelText.props.children);
          } else if (labelText && labelText.toString) {
            labelText = labelText.toString();
          } else {
            labelText = 'Block';
          }
        }
        // Remove any HTML tags if present
        labelText = labelText.replace(/<[^>]*>?/gm, '');
        const label = document.createElement('div');
        label.textContent = labelText;
        label.style.cssText = `
          flex: 1;
          font-size: 14px;
          font-weight: 500;
          color: #374151;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        `
        
        blockEl.appendChild(icon)
        blockEl.appendChild(label)
        
        blockEl.addEventListener('mouseenter', () => {
          blockEl.style.background = '#eff6ff'
          blockEl.style.borderColor = '#2563eb'
          blockEl.style.transform = 'translateX(4px)'
        })
        
        blockEl.addEventListener('mouseleave', () => {
          blockEl.style.background = 'transparent'
          blockEl.style.borderColor = 'transparent'
          blockEl.style.transform = 'translateX(0)'
        })
        
        blockEl.addEventListener('click', () => {
          insertBlock(block)
          hideContextMenu()
        })
        
        blocksContainer.appendChild(blockEl)
      })
    }
    
    renderBlocks()
    
    // Search functionality
    searchInput.addEventListener('input', (e) => {
      renderBlocks(e.target.value)
    })
    
    menu.appendChild(searchInput)
    menu.appendChild(blocksContainer)
    
    // Position menu
    try {
      let left = '50%';
      let top = '50%';
      let useTransform = false;
      if (currentFloatingButtons) {
        const rect = currentFloatingButtons.getBoundingClientRect();
        left = rect.right + 20;
        top = rect.top - 50;
        useTransform = false;
      } else {
        useTransform = true;
      }

      // Clamp to viewport, or center if off-screen
      const menuWidth = 280; // Fixed width
      const menuHeight = 400; // Max height
      const margin = 24;
      const winW = window.innerWidth;
      const winH = window.innerHeight;
      let outOfBounds = false;
      if (!useTransform) {
        if (left + menuWidth > winW - margin || left < margin || top + menuHeight > winH - margin || top < margin) {
          outOfBounds = true;
        }
        if (!outOfBounds) {
          if (left + menuWidth > winW - margin) left = winW - menuWidth - margin;
          if (left < margin) left = margin;
          if (top + menuHeight > winH - margin) top = winH - menuHeight - margin;
          if (top < margin) top = margin;
          menu.style.left = left + 'px';
          menu.style.top = top + 'px';
          menu.style.transform = '';
        } else {
          menu.style.left = '50%';
          menu.style.top = '50%';
          menu.style.transform = 'translate(-50%, -50%)';
        }
      } else {
        menu.style.left = '50%';
        menu.style.top = '50%';
        menu.style.transform = 'translate(-50%, -50%)';
      }

      document.body.appendChild(menu);
      currentContextMenu = menu;
    } catch (err) {
      console.error('🔴 Error creating block menu:', err);
    }
    
    // Focus search input
    setTimeout(() => {
      searchInput.focus()
    }, 10)
  }
  
  // Insert block immediately after the selected component, in the document
  // flow — the same place a normal drag-and-drop from the block manager
  // would put it. `domComponents.addComponent()` always adds to the
  // wrapper/root regardless of selection (see its own doc comment: "Add new
  // components to the wrapper's children"), which is why blocks used to
  // always land at the page root, absolutely positioned at a pixel guess of
  // where the selection was on screen — landing on top of whatever content
  // actually occupied that spot.
  function insertBlock(block) {
    if (!selectedComponent) return

    const parent = selectedComponent.parent()
    if (!parent) return

    const [component] = parent.append(block.get('content'), {
      at: selectedComponent.index() + 1
    })

    // Select the new component
    editor.select(component)

    console.log(`✅ Inserted block: ${block.get('label')}`)
  }
  
  // Show context menu
  function showContextMenu() {
    hideContextMenu()
    
    if (!selectedComponent) return
    
    const menu = document.createElement('div')
    menu.className = 'super-tooltip-context-menu'
    menu.style.cssText = `
      position: absolute;
      background: white;
      border: 1px solid #ddd;
      border-radius: 8px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.15);
      z-index: ${config.zIndex + 2};
      min-width: 200px;
      padding: 8px;
    `
    
    const menuItems = [
      { label: 'Duplicate (Cmd/Ctrl + S)', action: () => duplicateComponent() },
      { label: 'Delete (Del)', action: () => deleteComponent() },
      { label: 'Select Parent (Cmd/Ctrl + E)', action: () => selectParent() },
      { label: 'Move Up (Cmd/Ctrl + ↑)', action: () => moveComponent('up') },
      { label: 'Move Down (Cmd/Ctrl + ↓)', action: () => moveComponent('down') },
    ]
    
    menuItems.forEach(item => {
      const menuItem = document.createElement('div')
      menuItem.className = 'super-tooltip-menu-item'
      menuItem.textContent = item.label
      menuItem.style.cssText = `
        padding: 8px 12px;
        cursor: pointer;
        border-radius: 4px;
        font-size: 14px;
        transition: background 0.2s ease;
      `
      
      menuItem.addEventListener('mouseenter', () => {
        menuItem.style.background = '#f8f9fa'
      })
      
      menuItem.addEventListener('mouseleave', () => {
        menuItem.style.background = 'transparent'
      })
      
      menuItem.addEventListener('click', () => {
        item.action()
        hideContextMenu()
      })
      
      menu.appendChild(menuItem)
    })
    
    // Position menu
    if (currentFloatingButtons) {
      const rect = currentFloatingButtons.getBoundingClientRect()
      menu.style.left = `${rect.right + 10}px`
      menu.style.top = `${rect.top}px`
    }
    
    document.body.appendChild(menu)
    currentContextMenu = menu
  }
  
  // Hide context menu
  function hideContextMenu() {
    if (currentContextMenu) {
      currentContextMenu.remove()
      currentContextMenu = null
    }
  }
  
  // Component actions
  function duplicateComponent() {
    if (!selectedComponent) return
    const cloned = selectedComponent.clone()
    selectedComponent.parent().append(cloned)
    editor.select(cloned)
    console.log('✅ Component duplicated')
  }
  
  function deleteComponent() {
    if (!selectedComponent) return
    selectedComponent.remove()
    console.log('✅ Component deleted')
  }
  
  function selectParent() {
    if (!selectedComponent) return
    const parent = selectedComponent.parent()
    if (parent && parent.get('type') !== 'wrapper') {
      editor.select(parent)
      console.log('✅ Parent selected')
    }
  }
  
  function moveComponent(direction) {
    if (!selectedComponent) return
    const parent = selectedComponent.parent()
    if (!parent) return
    
    const siblings = parent.components().toArray()
    const currentIndex = siblings.indexOf(selectedComponent)
    
    if (direction === 'up' && currentIndex > 0) {
      parent.components().at(currentIndex - 1).before(selectedComponent)
      console.log('✅ Component moved up')
    } else if (direction === 'down' && currentIndex < siblings.length - 1) {
      parent.components().at(currentIndex + 1).after(selectedComponent)
      console.log('✅ Component moved down')
    }
  }
  
  // Returns true when the event's real target is a place the user is
  // actively typing (a form field or a contenteditable region) — global
  // shortcuts must never fire there, since `selectedComponent` stays set
  // for the whole session after any canvas selection, long after focus has
  // moved elsewhere in the app (search boxes, Settings fields, page names).
  function isEditableTarget(target) {
    if (!target) return false
    const tag = target.tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
    return !!target.isContentEditable
  }

  // Keyboard shortcuts
  function setupKeyboardShortcuts() {
    document.addEventListener('keydown', (e) => {
      if (!selectedComponent || isEditableTarget(e.target)) return

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
      const cmdKey = isMac ? e.metaKey : e.ctrlKey
      
      if (cmdKey && e.key === 'd') {
        e.preventDefault()
        showBlockInsertionMenu()
      } else if (cmdKey && e.key === 'f') {
        e.preventDefault()
        showContextMenu()
      } else if (cmdKey && e.key === 'e') {
        e.preventDefault()
        selectParent()
      } else if (cmdKey && e.key === 's') {
        e.preventDefault()
        duplicateComponent()
      } else if (cmdKey && e.key === 'ArrowUp') {
        e.preventDefault()
        moveComponent('up')
      } else if (cmdKey && e.key === 'ArrowDown') {
        e.preventDefault()
        moveComponent('down')
      } else if (e.key === 'Delete') {
        e.preventDefault()
        deleteComponent()
      }
    })
  }
  
  // Event listeners
  function setupEventListeners() {
    console.log('🎯 Setting up event listeners...')
    
    // Component selection
    editor.on('component:selected', (component) => {
      console.log('🎯 Component selected:', component)
      console.log('🎯 Component type:', component.get('type'))
      console.log('🎯 Component tagName:', component.get('tagName'))
      selectedComponent = component
      showFloatingButtons(component)
    })
    
    editor.on('component:deselected', () => {
      console.log('🎯 Component deselected')
      selectedComponent = null
      hideFloatingButtons()
      hideTooltip()
    })
    
    // Canvas click to hide menus - use editor events instead of canvas.on
    editor.on('canvas:click', () => {
      hideContextMenu()
    })
    
    // Document click to hide menus
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.super-tooltip-context-menu') && 
          !e.target.closest('.super-tooltip-block-menu') &&
          !e.target.closest('.super-tooltip-floating-buttons')) {
        hideContextMenu()
      }
    })
    
    console.log('✅ Event listeners set up')
  }
  
  // Initialize plugin
  function init() {
    console.log('🎯 Initializing Super Tooltip plugin...')
    setupEventListeners()
    setupKeyboardShortcuts()
    console.log('✅ Super Tooltip plugin initialized')
  }
  
  // Initialize when editor is ready
  if (editor.isReady) {
    console.log('🎯 Editor is ready, initializing Super Tooltip...')
    init()
  } else {
    console.log('🎯 Waiting for editor to load...')
    editor.on('load', () => {
      console.log('🎯 Editor loaded, initializing Super Tooltip...')
      init()
    })
  }
  
  // Add commands
  editor.Commands.add('super-tooltip:toggle', {
    run: (editor) => {
      config.enabled = !config.enabled
      console.log(`Super Tooltip ${config.enabled ? 'enabled' : 'disabled'}`)
    }
  })
  
  // Add manual test functions
  window.testSuperTooltip = () => {
    console.log('🧪 Testing Super Tooltip...')
    const selected = editor.getSelected()
    if (selected) {
      console.log('🧪 Selected component:', selected)
      showFloatingButtons(selected)
    } else {
      console.log('❌ No component selected')
    }
  }
  
  window.testFloatingButtons = () => {
    console.log('🧪 Testing floating buttons visibility...')
    const testDiv = document.createElement('div')
    testDiv.style.cssText = `
      position: fixed;
      top: 100px;
      left: 100px;
      width: 200px;
      height: 100px;
      background: red;
      color: white;
      z-index: 999999;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
    `
    testDiv.textContent = 'TEST BUTTONS VISIBLE'
    document.body.appendChild(testDiv)
    setTimeout(() => testDiv.remove(), 3000)
  }
  
  window.forceShowButtons = () => {
    console.log('🧪 Force showing buttons...')
    const testComponent = editor.getWrapper().components().at(0)
    if (testComponent) {
      showFloatingButtons(testComponent)
    }
  }
  
  window.testBlockMenu = () => {
    console.log('🧪 Testing block menu...')
    showBlockInsertionMenu()
  }
  
  console.log('✅ Super Tooltip plugin registered')
} 